import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { projects } from '../assets/projects';
import useReveal from '../hooks/useReveal';
import { useSmoothScroll } from '../motion/SmoothScroll';

const filterCategories = [
  { id: 'all', label: 'All Projects', count: null },
  { id: 'delhi', label: 'Delhi NCR', count: null },
  { id: 'noida', label: 'Noida', count: null },
  { id: 'residence', label: 'Private Residences', count: null },
];

const Portfolio = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const sectionRef = useRef(null);
  const { lenis } = useSmoothScroll() || {};

  useEffect(() => {
    if (lenis) {
      lenis.scrollTo(0, { immediate: true });
    } else if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    }
    window.scrollTo(0, 0);
  }, [lenis]);

  const filteredProjects = useMemo(() => {
    return projects.filter((item) => {
      if (activeFilter === 'all') return true;
      if (activeFilter === 'delhi') {
        return item.location.toLowerCase().includes('delhi');
      }
      if (activeFilter === 'noida') {
        return item.location.toLowerCase().includes('noida');
      }
      if (activeFilter === 'residence') {
        return item.title.toLowerCase().includes('residence');
      }
      return true;
    });
  }, [activeFilter]);

  useReveal(sectionRef, '.portfolio-card-luxury', { y: 32, stagger: 0.08 });

  return (
    <>
      <section className="apple-banner portfolio-hero-banner">
        <div className="apple-wrap apple-center">
          <span className="apple-eyebrow">Architectural Works</span>
          <h1 className="portfolio-title-display">
            Built with <span className="apple-italic">purpose</span>.
          </h1>
          <p className="portfolio-lede">
            Curated private residences, bespoke villas, and tailored architectural environments across Delhi and Noida.
          </p>

          {/* Filter Pills */}
          <div className="portfolio-filters" role="tablist">
            {filterCategories.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                className={`portfolio-filter-btn ${activeFilter === tab.id ? 'is-active' : ''}`}
                onClick={() => setActiveFilter(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="portfolio-section" ref={sectionRef}>
        <div className="portfolio-luxury-grid">
          <AnimatePresence>
            {filteredProjects.map((item, index) => {
              const hasMultiplePhotos = item.gallery && item.gallery.length > 1;
              const photoCount = item.gallery ? item.gallery.length : 1;

              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className="portfolio-card-luxury"
                >
                  <Link to={item.link} className="portfolio-card-inner">
                    {/* Media Container with zoom physics */}
                    <div className="portfolio-card-media">
                      <img
                        src={item.image}
                        alt={item.title}
                        loading="lazy"
                        className="portfolio-card-img"
                      />
                      <div className="portfolio-card-scrim" />

                      {/* Top Badges */}
                      <div className="portfolio-card-top-bar">
                        <span className="portfolio-num-badge">{item.number}</span>
                        {hasMultiplePhotos && (
                          <span className="portfolio-gallery-badge">
                            <i className="fas fa-camera" aria-hidden /> {photoCount} Views
                          </span>
                        )}
                      </div>

                      {/* Bottom Info Floating Glass Overlay */}
                      <div className="portfolio-card-glass-info">
                        <div className="portfolio-card-loc">
                          <i className="fas fa-map-pin" aria-hidden />
                          <span>{item.location}</span>
                        </div>
                        <h3 className="portfolio-card-heading">{item.title}</h3>

                        <div className="portfolio-card-action">
                          <span>Explore Project</span>
                          <div className="portfolio-arrow-circle">
                            <i className="fas fa-arrow-right" aria-hidden />
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {/* Bottom CTA Card */}
        <div className="apple-wrap--wide portfolio-bottom-cta">
          <div className="portfolio-cta-card">
            <div className="portfolio-cta-copy">
              <span className="cta-badge">Have a site in mind?</span>
              <h3>Let’s craft your bespoke architectural sanctuary.</h3>
              <p>Consult with our principal architects on spatial planning, zoning, and turnkey build costs.</p>
            </div>
            <a href="/#contact" className="portfolio-cta-btn">
              <span>Start a conversation</span>
              <i className="fas fa-arrow-right" aria-hidden />
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default Portfolio;

